import type {
  ModuleResponse,
  WorkspacePendingInvite,
  WorkspaceUserWithModules,
} from '@protoss/shared';
import {
  Button,
  MultiSelect,
  Pill,
  ResizableTable,
  Select,
  SelectItem,
  SkeletonBlock,
  TextField,
  Tooltip,
  type MultiSelectOption,
  type ResizableColumnDef,
} from '@protoss/ui';
import * as React from 'react';
import { PageErrorState } from '../components/shared/PageErrorState';
import {
  ContextDrawer,
  MainContentHeaderActionsContext,
  MainContentSidePanelContext,
} from '../components/shell';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { apiFetch } from '../lib/apiClient';
import './workspace-users-page.css';

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function WorkspaceUsersSkeleton() {
  return (
    <div className="wu-page" aria-busy="true" aria-label="Loading users">
      <div className="wu-skeleton-rows">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="wu-skeleton-row" aria-hidden="true">
            <SkeletonBlock />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Unified table row type ───────────────────────────────────────────────────

type WorkspaceTableRow =
  | (WorkspaceUserWithModules & { rowType: 'member' })
  | (WorkspacePendingInvite & { rowType: 'invite'; displayName: string; isAdmin: boolean });

// ─── Read-only module chips (table display) ───────────────────────────────────

const MODULE_ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  viewer: 'Viewer',
  collaborator: 'Collaborator',
};

function ModuleChipsReadOnly({
  keys,
  labelMap,
  roles,
}: {
  keys: string[];
  labelMap: Map<string, string>;
  roles?: Record<string, string>;
}) {
  if (keys.length === 0) {
    return <span className="wu-no-module">None</span>;
  }
  return (
    <div className="wu-chips">
      {keys.map((key) => (
        <span key={key} className="wu-chip">
          {roles?.[key]
            ? (MODULE_ROLE_LABELS[roles[key]] ?? roles[key])
            : (labelMap.get(key) ?? key)}
        </span>
      ))}
    </div>
  );
}

// ─── Module role config helpers ───────────────────────────────────────────────

function getRoleStatus(
  moduleKey: string,
  role: string,
  config: {
    [moduleKey: string]: { enabled?: string[]; comingSoon?: string[]; disabled?: string[] };
  } | null
): 'enabled' | 'coming_soon' | 'disabled' {
  if (!config) return 'enabled';

  const moduleConfig = config[moduleKey] ?? config['*'];
  if (!moduleConfig) return 'enabled';

  if (moduleConfig.disabled?.includes(role)) return 'disabled';
  if (moduleConfig.comingSoon?.includes(role)) return 'coming_soon';
  return 'enabled';
}

// ─── Single-user edit drawer content ─────────────────────────────────────────

interface SingleUserEditDrawerProps {
  user: WorkspaceUserWithModules;
  modules: { key: string; label: string }[];
  moduleRoleConfig: {
    [moduleKey: string]: { enabled?: string[]; comingSoon?: string[]; disabled?: string[] };
  } | null;
  onClose: () => void;
  onSaved: () => void;
}

const VALID_MODULE_ROLES = ['admin', 'manager', 'viewer', 'collaborator'] as const;
type ModuleRoleName = (typeof VALID_MODULE_ROLES)[number];

function SingleUserEditDrawer({
  user,
  modules,
  moduleRoleConfig,
  onClose,
  onSaved,
}: SingleUserEditDrawerProps) {
  const [isAdmin, setIsAdmin] = React.useState(user.isAdmin);
  const [selectedModules, setSelectedModules] = React.useState<string[]>(user.assignedModuleKeys);
  const [moduleRoles, setModuleRoles] = React.useState<Record<string, ModuleRoleName>>(() => {
    const roles: Record<string, ModuleRoleName> = {};
    for (const k of user.assignedModuleKeys) {
      const existing = (user.moduleRoles[k] as ModuleRoleName | undefined) ?? 'manager';
      const status = getRoleStatus(k, existing, moduleRoleConfig);
      roles[k] = status === 'enabled' ? existing : 'admin';
    }
    return roles;
  });
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  // Remove-access state. Self-removal is blocked server-side (CANNOT_REMOVE_SELF);
  // hiding the control for the signed-in admin keeps the UI honest about that.
  const { appUser } = useAuth();
  const isSelf = appUser?.uuid === user.uuid;
  const [confirmingRemove, setConfirmingRemove] = React.useState(false);
  const [isRemoving, setIsRemoving] = React.useState(false);
  const [removeError, setRemoveError] = React.useState<string | null>(null);

  const originalModules = user.assignedModuleKeys;
  const moduleOptions: MultiSelectOption[] = modules.map((m) => ({
    value: m.key,
    label: m.label,
  }));

  // When modules are added/removed, keep moduleRoles in sync
  const handleModulesChange = (keys: string[]) => {
    setSelectedModules(keys);
    setModuleRoles((prev) => {
      const next: Record<string, ModuleRoleName> = {};
      for (const k of keys) {
        next[k] = prev[k] ?? (user.moduleRoles[k] as ModuleRoleName | undefined) ?? 'manager';
      }
      return next;
    });
  };

  const roleChanged = isAdmin !== user.isAdmin;
  const originalSet = new Set(originalModules);
  const newSet = new Set(selectedModules);
  const toAdd = selectedModules.filter((k) => !originalSet.has(k));
  const toRemove = originalModules.filter((k) => !newSet.has(k));
  const modulesChanged = toAdd.length > 0 || toRemove.length > 0;
  const moduleRoleChanges = originalModules.filter(
    (k) =>
      newSet.has(k) &&
      moduleRoles[k] !== undefined &&
      moduleRoles[k] !== ((user.moduleRoles[k] as ModuleRoleName | undefined) ?? 'manager')
  );
  const isDirty = roleChanged || modulesChanged || moduleRoleChanges.length > 0;

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      if (roleChanged) {
        await apiFetch(`/api/workspace/admin/users/${encodeURIComponent(user.uuid)}/role`, {
          method: 'PUT',
          body: JSON.stringify({ isAdmin }),
        });
      }

      await Promise.all(
        toAdd.map((moduleKey) =>
          apiFetch('/api/workspace/admin/users/modules', {
            method: 'POST',
            body: JSON.stringify({ userUuids: [user.uuid], moduleKey }),
          })
        )
      );
      await Promise.all(
        toRemove.map((moduleKey) =>
          apiFetch(
            `/api/workspace/admin/users/${encodeURIComponent(user.uuid)}/modules/${encodeURIComponent(moduleKey)}`,
            { method: 'DELETE' }
          )
        )
      );

      // For newly added modules, set role if not the default (manager)
      const addedRolePatches = toAdd.filter(
        (k) => moduleRoles[k] !== undefined && moduleRoles[k] !== 'manager'
      );
      // For pre-existing modules, patch changed roles
      const existingRolePatches = moduleRoleChanges;

      await Promise.all(
        [...addedRolePatches, ...existingRolePatches].map((moduleKey) =>
          apiFetch(
            `/api/workspace/admin/users/${encodeURIComponent(user.uuid)}/modules/${encodeURIComponent(moduleKey)}/role`,
            {
              method: 'PATCH',
              body: JSON.stringify({ roleName: moduleRoles[moduleKey] }),
            }
          )
        )
      );

      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveAccess = async () => {
    setIsRemoving(true);
    setRemoveError(null);
    try {
      await apiFetch(`/api/workspace/admin/users/${encodeURIComponent(user.uuid)}`, {
        method: 'DELETE',
      });
      onSaved();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove access';
      setRemoveError(
        message === 'CANNOT_REMOVE_SELF' ? 'You cannot remove your own workspace access.' : message
      );
      setIsRemoving(false);
    }
  };

  return (
    <div className="wu-drawer-content">
      <div className="wu-drawer-user-identity">
        <span className="wu-drawer-user-name">{user.displayName}</span>
        <span className="wu-drawer-user-email">{user.email}</span>
      </div>

      <div className="wu-drawer-field">
        <span className="wu-drawer-field-label">Workspace Role</span>
        <div className="wu-role-toggle" role="group" aria-label="Workspace role">
          <button
            type="button"
            className="wu-role-toggle-btn"
            data-active={!isAdmin ? 'true' : 'false'}
            onClick={() => setIsAdmin(false)}
            disabled={isSaving}
          >
            Member
          </button>
          <button
            type="button"
            className="wu-role-toggle-btn"
            data-active={isAdmin ? 'true' : 'false'}
            onClick={() => setIsAdmin(true)}
            disabled={isSaving}
          >
            Admin
          </button>
        </div>
      </div>

      {modules.length > 1 && (
        <div className="wu-drawer-field">
          <MultiSelect
            label="Assigned Modules"
            options={moduleOptions}
            selectedValues={selectedModules}
            originalValues={originalModules}
            onSelectionChange={handleModulesChange}
            disabled={isSaving}
            placeholder="Search modules…"
          />
        </div>
      )}

      {selectedModules.map((moduleKey) => {
        const moduleLabel = modules.find((m) => m.key === moduleKey)?.label ?? moduleKey;
        return (
          <div key={moduleKey} className="wu-drawer-field">
            <span className="wu-drawer-field-label">{moduleLabel} Role</span>
            <span className="wu-drawer-field-desc">
              Admin: full control. Manager: create &amp; edit. Viewer: read only. Collaborator:
              limited.
            </span>
            <Select
              value={moduleRoles[moduleKey] ?? 'manager'}
              onValueChange={(value) =>
                setModuleRoles((prev) => ({
                  ...prev,
                  [moduleKey]: value as ModuleRoleName,
                }))
              }
              disabled={isSaving}
              size="comfortable"
            >
              {getRoleStatus(moduleKey, 'admin', moduleRoleConfig) !== 'disabled' && (
                <SelectItem value="admin">Admin</SelectItem>
              )}
              {['manager', 'viewer', 'collaborator'].map((role) => {
                const status = getRoleStatus(moduleKey, role, moduleRoleConfig);
                if (status === 'disabled') return null;
                const isComingSoon = status === 'coming_soon';
                const label = isComingSoon
                  ? `${MODULE_ROLE_LABELS[role]} (Coming soon)`
                  : MODULE_ROLE_LABELS[role];

                if (isComingSoon) {
                  return (
                    <Tooltip.Root key={role}>
                      <Tooltip.Trigger asChild>
                        <div>
                          <SelectItem value={role} disabled>
                            {label}
                          </SelectItem>
                        </div>
                      </Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content side="right" sideOffset={4} className="ui-tooltip">
                          Coming soon
                          <Tooltip.Arrow className="ui-tooltip-arrow" />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  );
                }

                return (
                  <SelectItem key={role} value={role}>
                    {label}
                  </SelectItem>
                );
              })}
            </Select>
          </div>
        );
      })}

      {saveError && <p className="wu-drawer-error">{saveError}</p>}

      <div className="wu-drawer-actions">
        <button
          type="button"
          className="wu-drawer-btn wu-drawer-btn-primary"
          onClick={handleSave}
          disabled={!isDirty || isSaving}
        >
          {isSaving ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          className="wu-drawer-btn wu-drawer-btn-secondary"
          onClick={onClose}
          disabled={isSaving}
        >
          Cancel
        </button>
      </div>

      {!isSelf && (
        <div className="wu-drawer-danger-zone">
          <span className="wu-drawer-field-label">Remove access</span>
          <span className="wu-drawer-field-desc">
            Removes this user from the workspace and revokes access to all modules. Their account
            and history are kept, and they can be invited again later.
          </span>
          {confirmingRemove ? (
            <div className="wu-drawer-danger-confirm">
              <span className="wu-drawer-danger-confirm-text">
                Remove {user.displayName || user.email} from this workspace?
              </span>
              <div className="wu-drawer-actions">
                <button
                  type="button"
                  className="wu-drawer-btn wu-drawer-btn-danger"
                  onClick={handleRemoveAccess}
                  disabled={isRemoving}
                >
                  {isRemoving ? 'Removing…' : 'Remove access'}
                </button>
                <button
                  type="button"
                  className="wu-drawer-btn wu-drawer-btn-secondary"
                  onClick={() => setConfirmingRemove(false)}
                  disabled={isRemoving}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="wu-drawer-btn wu-drawer-btn-danger-outline"
              onClick={() => setConfirmingRemove(true)}
              disabled={isSaving}
            >
              Remove access
            </button>
          )}
          {removeError && <p className="wu-drawer-error">{removeError}</p>}
        </div>
      )}
    </div>
  );
}

// ─── Bulk-edit drawer content ─────────────────────────────────────────────────

interface BulkEditDrawerProps {
  userUuids: string[];
  modules: { key: string; label: string }[];
  onClose: () => void;
  onSaved: () => void;
}

function BulkEditDrawer({ userUuids, modules, onClose, onSaved }: BulkEditDrawerProps) {
  const [modulesToAdd, setModulesToAdd] = React.useState<string[]>([]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const moduleOptions: MultiSelectOption[] = modules.map((m) => ({
    value: m.key,
    label: m.label,
  }));

  const handleSave = async () => {
    if (modulesToAdd.length === 0) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await Promise.all(
        modulesToAdd.map((moduleKey) =>
          apiFetch('/api/workspace/admin/users/modules', {
            method: 'POST',
            body: JSON.stringify({ userUuids, moduleKey }),
          })
        )
      );
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to assign modules');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="wu-drawer-content">
      <p className="wu-drawer-bulk-note">
        Module assignment only. Role changes are not supported for bulk edits - edit users
        individually to change workspace roles.
      </p>

      <div className="wu-drawer-field">
        <MultiSelect
          label="Add Modules"
          options={moduleOptions}
          selectedValues={modulesToAdd}
          onSelectionChange={setModulesToAdd}
          disabled={isSaving}
          placeholder="Search modules to add…"
        />
      </div>

      {saveError && <p className="wu-drawer-error">{saveError}</p>}

      <div className="wu-drawer-actions">
        <button
          type="button"
          className="wu-drawer-btn wu-drawer-btn-primary"
          onClick={handleSave}
          disabled={modulesToAdd.length === 0 || isSaving}
        >
          {isSaving ? 'Adding…' : 'Add modules'}
        </button>
        <button
          type="button"
          className="wu-drawer-btn wu-drawer-btn-secondary"
          onClick={onClose}
          disabled={isSaving}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Shared invite error handler ─────────────────────────────────────────────

function formatInviteError(message: string): string {
  if (message === 'USER_ALREADY_MEMBER')
    return 'That user is already an active member of this workspace.';
  if (message === 'EMAIL_NOT_CONFIGURED' || message === 'FIREBASE_EMAIL_NOT_CONFIGURED')
    return 'Invitation email delivery is not configured for this deployment yet.';
  if (message === 'WORKSPACE_INVITE_REDIRECT_URL_INVALID')
    return 'The workspace invite redirect URL is invalid. Update the Branding settings before sending invites.';
  if (message.includes('UNAUTHORIZED_DOMAIN'))
    return 'Firebase rejected the invite redirect domain. Update the workspace invite redirect URL in Branding settings and make sure that domain is added to Firebase Authorized Domains.';
  if (message.startsWith('INVALID_MODULES'))
    return 'One or more selected modules are not enabled for this workspace.';
  if (message === 'INVITE_DOMAIN_NOT_ALLOWED')
    return 'You can only invite users on your own email domain.';
  return message;
}

// ─── Invited-user edit drawer (resend) ────────────────────────────────────────

interface InviteEditDrawerProps {
  invite: WorkspacePendingInvite;
  modules: { key: string; label: string }[];
  moduleRoleConfig: {
    [moduleKey: string]: { enabled?: string[]; comingSoon?: string[]; disabled?: string[] };
  } | null;
  onClose: () => void;
  onSaved: () => void;
}

function InviteEditDrawer({
  invite,
  modules,
  moduleRoleConfig,
  onClose,
  onSaved,
}: InviteEditDrawerProps) {
  const [workspaceRole, setWorkspaceRole] = React.useState<'workspace_member' | 'workspace_admin'>(
    invite.workspaceRole
  );
  const [selectedModules, setSelectedModules] = React.useState<string[]>(invite.assignedModuleKeys);
  const [moduleRoles, setModuleRoles] = React.useState<Record<string, ModuleRoleName>>(() => {
    const roles: Record<string, ModuleRoleName> = {};
    for (const k of invite.assignedModuleKeys) {
      const existing = (invite.moduleRoles[k] as ModuleRoleName | undefined) ?? 'manager';
      const status = getRoleStatus(k, existing, moduleRoleConfig);
      roles[k] = status === 'enabled' ? existing : 'admin';
    }
    return roles;
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = React.useState(false);

  const moduleOptions: MultiSelectOption[] = modules.map((m) => ({ value: m.key, label: m.label }));

  const handleModulesChange = (keys: string[]) => {
    setSelectedModules(keys);
    setModuleRoles((prev) => {
      const next: Record<string, ModuleRoleName> = {};
      for (const k of keys) {
        const existing = prev[k] ?? 'manager';
        const status = getRoleStatus(k, existing, moduleRoleConfig);
        next[k] = status === 'enabled' ? existing : 'admin';
      }
      return next;
    });
  };

  const handleResend = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    setResendSuccess(false);
    try {
      await apiFetch('/api/workspace/admin/users/invite', {
        method: 'POST',
        body: JSON.stringify({
          email: invite.email,
          workspaceRole,
          moduleKeys: selectedModules,
          moduleRoles,
        }),
      });
      setResendSuccess(true);
      await onSaved();
    } catch (error) {
      setSubmitError(
        formatInviteError(error instanceof Error ? error.message : 'Failed to resend invite')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="wu-drawer-content">
      <div className="wu-drawer-user-identity">
        <span className="wu-drawer-user-email">{invite.email}</span>
        <Pill variant="success">Invited</Pill>
      </div>

      <div className="wu-drawer-field">
        <span className="wu-drawer-field-label">Workspace Role</span>
        <span className="wu-drawer-field-desc">
          Member: access to assigned modules only. Admin: manage workspace settings and users.
        </span>
        <div className="wu-role-toggle" role="group" aria-label="Workspace role">
          <button
            type="button"
            className="wu-role-toggle-btn"
            data-active={workspaceRole === 'workspace_member' ? 'true' : 'false'}
            onClick={() => setWorkspaceRole('workspace_member')}
            disabled={isSubmitting}
          >
            Member
          </button>
          <button
            type="button"
            className="wu-role-toggle-btn"
            data-active={workspaceRole === 'workspace_admin' ? 'true' : 'false'}
            onClick={() => setWorkspaceRole('workspace_admin')}
            disabled={isSubmitting}
          >
            Admin
          </button>
        </div>
      </div>

      {modules.length > 1 && (
        <div className="wu-drawer-field">
          <MultiSelect
            label="Assigned Modules"
            options={moduleOptions}
            selectedValues={selectedModules}
            onSelectionChange={handleModulesChange}
            disabled={isSubmitting}
            placeholder="Search modules…"
          />
        </div>
      )}

      {selectedModules.map((moduleKey) => {
        const moduleLabel = modules.find((m) => m.key === moduleKey)?.label ?? moduleKey;
        return (
          <div key={moduleKey} className="wu-drawer-field">
            <span className="wu-drawer-field-label">{moduleLabel} Role</span>
            <span className="wu-drawer-field-desc">
              Admin: full control. Manager: create &amp; edit. Viewer: read only. Collaborator:
              limited.
            </span>
            <Select
              value={moduleRoles[moduleKey] ?? 'manager'}
              onValueChange={(value) =>
                setModuleRoles((prev) => ({ ...prev, [moduleKey]: value as ModuleRoleName }))
              }
              disabled={isSubmitting}
              size="comfortable"
            >
              {getRoleStatus(moduleKey, 'admin', moduleRoleConfig) !== 'disabled' && (
                <SelectItem value="admin">Admin</SelectItem>
              )}
              {['manager', 'viewer', 'collaborator'].map((role) => {
                const status = getRoleStatus(moduleKey, role, moduleRoleConfig);
                if (status === 'disabled') return null;
                const isComingSoon = status === 'coming_soon';
                const label = isComingSoon
                  ? `${MODULE_ROLE_LABELS[role]} (Coming soon)`
                  : MODULE_ROLE_LABELS[role];

                if (isComingSoon) {
                  return (
                    <Tooltip.Root key={role}>
                      <Tooltip.Trigger asChild>
                        <div>
                          <SelectItem value={role} disabled>
                            {label}
                          </SelectItem>
                        </div>
                      </Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content side="right" sideOffset={4} className="ui-tooltip">
                          Coming soon
                          <Tooltip.Arrow className="ui-tooltip-arrow" />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  );
                }

                return (
                  <SelectItem key={role} value={role}>
                    {label}
                  </SelectItem>
                );
              })}
            </Select>
          </div>
        );
      })}

      {submitError && <p className="wu-drawer-error">{submitError}</p>}
      {resendSuccess && <p className="wu-drawer-success">Invite resent successfully.</p>}

      <div className="wu-drawer-actions">
        <button
          type="button"
          className="wu-drawer-btn wu-drawer-btn-primary"
          onClick={handleResend}
          disabled={isSubmitting || selectedModules.length === 0}
        >
          {isSubmitting ? 'Sending…' : 'Resend Invite'}
        </button>
        <button
          type="button"
          className="wu-drawer-btn wu-drawer-btn-secondary"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Invite user drawer ───────────────────────────────────────────────────────

interface InviteUserDrawerProps {
  modules: { key: string; label: string }[];
  moduleRoleConfig: {
    [moduleKey: string]: { enabled?: string[]; comingSoon?: string[]; disabled?: string[] };
  } | null;
  onClose: () => void;
  onSaved: () => void;
}

function InviteUserDrawer({ modules, moduleRoleConfig, onClose, onSaved }: InviteUserDrawerProps) {
  const [email, setEmail] = React.useState('');
  const [emailTouched, setEmailTouched] = React.useState(false);
  const [emailServerError, setEmailServerError] = React.useState<string | null>(null);
  const [isCheckingEmail, setIsCheckingEmail] = React.useState(false);
  const [workspaceRole, setWorkspaceRole] = React.useState<'workspace_member' | 'workspace_admin'>(
    'workspace_member'
  );
  const [selectedModules, setSelectedModules] = React.useState<string[]>(
    modules.length === 1 ? [modules[0]!.key] : []
  );
  const [moduleRoles, setModuleRoles] = React.useState<Record<string, ModuleRoleName>>(() => {
    const roles: Record<string, ModuleRoleName> = {};
    if (modules.length === 1) {
      const key = modules[0]!.key;
      const defaultRole =
        getRoleStatus(key, 'manager', moduleRoleConfig) === 'enabled' ? 'manager' : 'admin';
      roles[key] = defaultRole;
    }
    return roles;
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const moduleOptions: MultiSelectOption[] = modules.map((module) => ({
    value: module.key,
    label: module.label,
  }));

  const emailFormatError = React.useMemo(() => {
    if (!email.trim()) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? undefined : 'Enter a valid email address';
  }, [email]);

  const emailError = emailTouched
    ? (emailFormatError ?? emailServerError ?? undefined)
    : (emailServerError ?? undefined);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setEmailServerError(null);
  };

  const handleEmailBlur = React.useCallback(async () => {
    setEmailTouched(true);
    if (emailFormatError) return;
    const trimmed = email.trim();
    if (!trimmed) return;
    setIsCheckingEmail(true);
    try {
      const result = await apiFetch<{ status: string }>(
        `/api/workspace/admin/users/check-email?email=${encodeURIComponent(trimmed)}`
      );
      if (result.status === 'already_member') {
        setEmailServerError('This person is already a workspace member');
      } else if (result.status === 'pending_invite') {
        setEmailServerError('An invite has already been sent to this email');
      } else if (result.status === 'domain_not_allowed') {
        // Domain isolation is on for this workspace: an admin may only invite their own
        // email domain. The server refuses the invite too, so this is inline feedback
        // rather than the gate itself.
        setEmailServerError('You can only invite users on your own email domain');
      }
    } catch {
      // silently ignore - submit will catch it if the server rejects
    } finally {
      setIsCheckingEmail(false);
    }
  }, [email, emailFormatError]);

  const handleModulesChange = (keys: string[]) => {
    setSelectedModules(keys);
    setModuleRoles((prev) => {
      const next: Record<string, ModuleRoleName> = {};
      for (const k of keys) {
        const existing = prev[k] ?? 'manager';
        const status = getRoleStatus(k, existing, moduleRoleConfig);
        next[k] = status === 'enabled' ? existing : 'admin';
      }
      return next;
    });
  };

  const canSubmit =
    !isSubmitting &&
    !isCheckingEmail &&
    !emailFormatError &&
    !emailServerError &&
    selectedModules.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await apiFetch('/api/workspace/admin/users/invite', {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          workspaceRole,
          moduleKeys: selectedModules,
          moduleRoles,
        }),
      });
      onSaved();
    } catch (error) {
      setSubmitError(
        formatInviteError(error instanceof Error ? error.message : 'Failed to send invite')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="wu-drawer-content">
      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={handleEmailChange}
        onBlur={handleEmailBlur}
        placeholder="person@company.com"
        error={emailError}
        disabled={isSubmitting}
        required
        size="comfortable"
      />

      <div className="wu-drawer-field">
        <span className="wu-drawer-field-label">Workspace Role</span>
        <span className="wu-drawer-field-desc">
          Member: access to assigned modules only. Admin: manage workspace settings and users.
        </span>
        <div className="wu-role-toggle" role="group" aria-label="Workspace role">
          <button
            type="button"
            className="wu-role-toggle-btn"
            data-active={workspaceRole === 'workspace_member' ? 'true' : 'false'}
            onClick={() => setWorkspaceRole('workspace_member')}
            disabled={isSubmitting}
          >
            Member
          </button>
          <button
            type="button"
            className="wu-role-toggle-btn"
            data-active={workspaceRole === 'workspace_admin' ? 'true' : 'false'}
            onClick={() => setWorkspaceRole('workspace_admin')}
            disabled={isSubmitting}
          >
            Admin
          </button>
        </div>
      </div>

      {modules.length > 1 && (
        <div className="wu-drawer-field">
          <MultiSelect
            label="Assigned Modules"
            options={moduleOptions}
            selectedValues={selectedModules}
            onSelectionChange={handleModulesChange}
            disabled={isSubmitting}
            placeholder="Search modules…"
          />
        </div>
      )}

      {selectedModules.map((moduleKey) => {
        const moduleLabel = modules.find((m) => m.key === moduleKey)?.label ?? moduleKey;
        return (
          <div key={moduleKey} className="wu-drawer-field">
            <span className="wu-drawer-field-label">{moduleLabel} Role</span>
            <span className="wu-drawer-field-desc">
              Admin: full control. Manager: create &amp; edit. Viewer: read only. Collaborator:
              limited.
            </span>
            <Select
              value={moduleRoles[moduleKey] ?? 'manager'}
              onValueChange={(value) =>
                setModuleRoles((prev) => ({ ...prev, [moduleKey]: value as ModuleRoleName }))
              }
              disabled={isSubmitting}
              size="comfortable"
            >
              {getRoleStatus(moduleKey, 'admin', moduleRoleConfig) !== 'disabled' && (
                <SelectItem value="admin">Admin</SelectItem>
              )}
              {['manager', 'viewer', 'collaborator'].map((role) => {
                const status = getRoleStatus(moduleKey, role, moduleRoleConfig);
                if (status === 'disabled') return null;
                const isComingSoon = status === 'coming_soon';
                const label = isComingSoon
                  ? `${MODULE_ROLE_LABELS[role]} (Coming soon)`
                  : MODULE_ROLE_LABELS[role];

                if (isComingSoon) {
                  return (
                    <Tooltip.Root key={role}>
                      <Tooltip.Trigger asChild>
                        <div>
                          <SelectItem value={role} disabled>
                            {label}
                          </SelectItem>
                        </div>
                      </Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content side="right" sideOffset={4} className="ui-tooltip">
                          Coming soon
                          <Tooltip.Arrow className="ui-tooltip-arrow" />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  );
                }

                return (
                  <SelectItem key={role} value={role}>
                    {label}
                  </SelectItem>
                );
              })}
            </Select>
          </div>
        );
      })}

      {submitError && <p className="wu-drawer-error">{submitError}</p>}

      <div className="wu-drawer-actions">
        <button
          type="button"
          className="wu-drawer-btn wu-drawer-btn-primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {isSubmitting ? 'Sending…' : 'Send invite'}
        </button>
        <button
          type="button"
          className="wu-drawer-btn wu-drawer-btn-secondary"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── WorkspaceUsersPage ───────────────────────────────────────────────────────

type DrawerMode = 'single' | 'bulk' | 'invite' | 'invite-edit';

export function WorkspaceUsersPage() {
  const { refetchModules } = useWorkspace();
  const [modules, setModules] = React.useState<ModuleResponse[]>([]);
  const [moduleRoleConfig, setModuleRoleConfig] = React.useState<{
    [moduleKey: string]: { enabled?: string[]; comingSoon?: string[]; disabled?: string[] };
  } | null>(null);

  const [users, setUsers] = React.useState<WorkspaceUserWithModules[]>([]);
  const [invites, setInvites] = React.useState<WorkspacePendingInvite[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  // Row selection
  const [selectedUuids, setSelectedUuids] = React.useState<ReadonlySet<string>>(new Set());

  // Drawer state
  const [drawerMode, setDrawerMode] = React.useState<DrawerMode | null>(null);
  const [drawerUserUuid, setDrawerUserUuid] = React.useState<string | null>(null);
  const [drawerInviteUuid, setDrawerInviteUuid] = React.useState<string | null>(null);

  // ─── Data loading ────────────────────────────────────────────────────────

  const loadUsers = React.useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [
        { users: data },
        { modules: allModules, moduleRoleConfig: config },
        { invites: pendingInvites },
      ] = await Promise.all([
        apiFetch<{ users: WorkspaceUserWithModules[] }>('/api/workspace/admin/users'),
        apiFetch<{
          modules: ModuleResponse[];
          moduleRoleConfig: {
            [moduleKey: string]: { enabled?: string[]; comingSoon?: string[]; disabled?: string[] };
          } | null;
        }>('/api/workspace/admin/modules'),
        apiFetch<{ invites: WorkspacePendingInvite[] }>('/api/workspace/admin/invites'),
      ]);
      setUsers(data);
      setModules(allModules);
      setModuleRoleConfig(config);
      setInvites(pendingInvites);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // ─── Drawer handlers ──────────────────────────────────────────────────────

  const openSingleDrawer = (uuid: string) => {
    setDrawerUserUuid(uuid);
    setDrawerMode('single');
  };

  const openBulkDrawer = () => {
    setDrawerMode('bulk');
  };

  const openInviteDrawer = () => {
    setDrawerMode('invite');
  };

  const openInviteEditDrawer = (inviteUuid: string) => {
    setDrawerInviteUuid(inviteUuid);
    setDrawerMode('invite-edit');
  };

  const closeDrawer = () => {
    setDrawerMode(null);
    setDrawerUserUuid(null);
    setDrawerInviteUuid(null);
  };

  const handleSaved = async () => {
    closeDrawer();
    setSelectedUuids(new Set());
    await Promise.all([loadUsers(), refetchModules()]);
  };

  // ─── Selection helpers ────────────────────────────────────────────────────

  const toggleSelectAll = () => {
    setSelectedUuids(
      selectedUuids.size === users.length ? new Set() : new Set(users.map((u) => u.uuid))
    );
  };

  const toggleSelectUser = (uuid: string, e: React.MouseEvent | React.ChangeEvent) => {
    e.stopPropagation();
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  };

  // ─── Derived ─────────────────────────────────────────────────────────────

  const moduleMap = React.useMemo(() => new Map(modules.map((m) => [m.key, m.label])), [modules]);

  const rows = React.useMemo<WorkspaceTableRow[]>(() => {
    const memberRows: WorkspaceTableRow[] = users.map((u) => ({ ...u, rowType: 'member' }));
    const inviteRows: WorkspaceTableRow[] = invites.map((inv) => ({
      ...inv,
      rowType: 'invite',
      displayName: '',
      isAdmin: inv.workspaceRole === 'workspace_admin',
    }));
    return [...memberRows, ...inviteRows];
  }, [users, invites]);

  const selectedCount = selectedUuids.size;
  const allSelected = users.length > 0 && selectedCount === users.length;
  const someSelected = selectedCount > 0 && selectedCount < users.length;

  const drawerUser = drawerUserUuid != null ? users.find((u) => u.uuid === drawerUserUuid) : null;
  const drawerInvite =
    drawerInviteUuid != null ? invites.find((i) => i.uuid === drawerInviteUuid) : null;

  const drawerOpen = drawerMode !== null;
  const resolvedDrawerTitle =
    drawerMode === 'invite'
      ? 'Invite User'
      : drawerMode === 'invite-edit'
        ? 'Edit User'
        : drawerMode === 'bulk'
          ? 'Edit Users'
          : 'Edit User';
  const drawerSubtitle =
    drawerMode === 'bulk'
      ? `${selectedCount} user${selectedCount !== 1 ? 's' : ''} selected`
      : undefined;

  // ─── Project header actions into MainContentShell ────────────────────────

  const headerActionsCtx = React.useContext(MainContentHeaderActionsContext);

  React.useLayoutEffect(() => {
    if (!headerActionsCtx) return;
    if (isLoading) {
      headerActionsCtx.setHeaderActions(
        <>
          <div className="wu-skeleton-header-btn">
            <SkeletonBlock />
          </div>
          <div className="wu-skeleton-header-btn wu-skeleton-header-btn--wide">
            <SkeletonBlock />
          </div>
        </>
      );
    } else {
      headerActionsCtx.setHeaderActions(
        <>
          <Button type="button" variant="outline" size="sm" onClick={() => void loadUsers()}>
            Refresh
          </Button>
          <Button type="button" variant="primary" size="sm" onClick={openInviteDrawer}>
            Invite User
          </Button>
        </>
      );
    }
    return () => {
      headerActionsCtx.setHeaderActions(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerActionsCtx, loadUsers, isLoading]);

  // ─── Project side panel into MainContentShell ───────────────────────────────────

  const sidePanelCtx = React.useContext(MainContentSidePanelContext);

  React.useLayoutEffect(() => {
    if (!sidePanelCtx) return;
    sidePanelCtx.setPanel(
      <ContextDrawer
        inline
        open={drawerOpen}
        onClose={closeDrawer}
        title={resolvedDrawerTitle}
        subtitle={drawerSubtitle}
      >
        {drawerMode === 'single' && drawerUser != null && (
          <SingleUserEditDrawer
            key={drawerUser.uuid}
            user={drawerUser}
            modules={modules}
            moduleRoleConfig={moduleRoleConfig}
            onClose={closeDrawer}
            onSaved={handleSaved}
          />
        )}
        {drawerMode === 'bulk' && (
          <BulkEditDrawer
            key={[...selectedUuids].sort().join(',')}
            userUuids={[...selectedUuids]}
            modules={modules}
            onClose={closeDrawer}
            onSaved={handleSaved}
          />
        )}
        {drawerMode === 'invite' && (
          <InviteUserDrawer
            modules={modules}
            moduleRoleConfig={moduleRoleConfig}
            onClose={closeDrawer}
            onSaved={handleSaved}
          />
        )}
        {drawerMode === 'invite-edit' && drawerInvite != null && (
          <InviteEditDrawer
            key={drawerInvite.uuid}
            invite={drawerInvite}
            modules={modules}
            moduleRoleConfig={moduleRoleConfig}
            onClose={closeDrawer}
            onSaved={handleSaved}
          />
        )}
      </ContextDrawer>
    );
    return () => {
      sidePanelCtx.setPanel(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sidePanelCtx,
    drawerOpen,
    drawerMode,
    drawerUser,
    drawerInvite,
    resolvedDrawerTitle,
    drawerSubtitle,
    modules,
    selectedUuids,
  ]);

  // ─── Column definitions ───────────────────────────────────────────────────

  const columns: ResizableColumnDef<WorkspaceTableRow>[] = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          className="rt-table__checkbox"
          checked={allSelected}
          ref={(el) => {
            if (el) el.indeterminate = someSelected;
          }}
          onChange={toggleSelectAll}
          onClick={(e) => e.stopPropagation()}
          aria-label="Select all users"
        />
      ),
      defaultWidth: 40,
      minWidth: 40,
      maxWidth: 40,
      headerClassName: 'rt-table__col--checkbox',
      cellClassName: 'rt-table__col--checkbox',
      stopRowClick: true,
      render: (row) =>
        row.rowType === 'member' ? (
          <input
            type="checkbox"
            className="rt-table__checkbox"
            checked={selectedUuids.has(row.uuid)}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => toggleSelectUser(row.uuid, e)}
            aria-label={`Select ${row.displayName}`}
          />
        ) : null,
    },
    {
      key: 'name',
      label: 'Name',
      defaultWidth: 160,
      render: (row) => <span className="wu-name">{row.displayName}</span>,
    },
    {
      key: 'email',
      label: 'Email',
      defaultWidth: 220,
      render: (row) => <span className="wu-email">{row.email}</span>,
    },
    {
      key: 'role',
      label: 'Workspace Role',
      defaultWidth: 140,
      render: (row) => (
        <Pill variant={row.isAdmin ? 'primary' : 'neutral'}>
          {row.isAdmin ? 'Admin' : 'Member'}
        </Pill>
      ),
    },
    {
      key: 'modules',
      label: 'Module Role',
      defaultWidth: 120,
      wrap: true,
      render: (row) => (
        <ModuleChipsReadOnly
          keys={row.assignedModuleKeys}
          labelMap={moduleMap}
          roles={row.moduleRoles}
        />
      ),
    },
    {
      key: 'status',
      label: 'Status',
      defaultWidth: 100,
      render: (row) => (row.rowType === 'invite' ? <Pill variant="success">Invited</Pill> : null),
    },
  ];

  // ─── Loading / error states ───────────────────────────────────────────────

  if (isLoading) {
    return <WorkspaceUsersSkeleton />;
  }

  if (loadError) {
    return <PageErrorState message={loadError} onRetry={loadUsers} />;
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="wu-page">
      {/* Bulk action toolbar - visible when rows are selected */}
      {selectedCount > 0 && (
        <div className="wu-bulk-toolbar" role="toolbar" aria-label="Bulk user actions">
          <span className="wu-bulk-count">
            {selectedCount} user{selectedCount !== 1 ? 's' : ''} selected
          </span>
          <button type="button" className="wu-bulk-edit-btn" onClick={openBulkDrawer}>
            Edit {selectedCount} user{selectedCount !== 1 ? 's' : ''}
          </button>
          <button
            type="button"
            className="wu-bulk-clear-btn"
            onClick={() => setSelectedUuids(new Set())}
          >
            Clear
          </button>
        </div>
      )}

      {/* Users table */}
      <ResizableTable
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.uuid}
        aria-label="Workspace users"
        hasSelectionColumn
        onRowClick={(row) => {
          if (row.rowType === 'member') openSingleDrawer(row.uuid);
          else if (row.rowType === 'invite') openInviteEditDrawer(row.uuid);
        }}
        getRowClassName={(row) =>
          row.rowType === 'member' && selectedUuids.has(row.uuid) ? 'wu-tr--selected' : undefined
        }
        emptyState={<div className="wu-empty">No workspace members found.</div>}
      />
    </div>
  );
}
